import React, { Component } from "react";
import {
  loading,
  NavbarAdmin,
  GET,
  format_date,
  DateRangePicker,
  t // นำเข้าฟังก์ชันแปลภาษา
} from "../../components/CustomComponent.js"; // ปรับ Path ให้ตรงกับโปรเจกต์ของคุณ

export default class AssetReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data_assets: [],
      data_work_orders: [],
      start_date: this.getDefaultStartDate(),
      end_date: this.getDefaultEndDate(),
    };
  }

  async componentDidMount() {
    loading(true);
    const [assetResult, woResult] = await Promise.all([
      GET("admin/assets", { page: 0 }),
      GET("admin/work-orders", { page: 0 }),
    ]);

    this.setState({
      data_assets: assetResult?.status ? assetResult.data || [] : [],
      data_work_orders: woResult?.status ? woResult.data || [] : [],
    });
    loading(false);
  }

  getDefaultStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  getDefaultEndDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  toDateOnly = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // ใบสั่งงานที่ถูกสร้างในช่วงวันที่ที่เลือก ใช้เป็นฐานคำนวณต้นทุน/สถิติของรายงาน
  getFilteredWorkOrders = () => {
    const { data_work_orders, start_date, end_date } = this.state;
    const rangeStart = this.toDateOnly(start_date);
    const rangeEnd = this.toDateOnly(end_date || start_date);
    if (!rangeStart || !rangeEnd) return data_work_orders;

    return data_work_orders.filter((item) => {
      const createdDate = this.toDateOnly(item.created_at);
      if (!createdDate) return false;
      return createdDate >= rangeStart && createdDate <= rangeEnd;
    });
  };

  // logic เดียวกับ work-order-summary.js เพื่อจัดกลุ่มสถานะใบสั่งงาน
  getStatusBucketId = (item) => {
    const status = String(item?.status || "").trim().toLowerCase();

    if (["cancelled", "rejected"].includes(status)) return "cancelled";
    if (["completed", "success", "approved", "closed"].includes(status)) return "completed";
    if (status === "en_route") return "en_route";
    if (["in_progress", "on_site", "working"].includes(status)) return "in_progress";

    const hasAssignedTechnician = (item?.visits || []).some(
      (visit) => (visit?.visits_technicians || []).filter(Boolean).length > 0
    );
    if (["assigned", "scheduled"].includes(status) || hasAssignedTechnician) return "assigned";

    return "open";
  };

  // ทรัพย์สินที่ "กำลังซ่อมบำรุง" = มีใบสั่งงานที่ยังไม่ completed/cancelled อยู่ ณ ปัจจุบัน (ไม่จำกัดตามช่วงวันที่)
  getOpenWorkOrderAssetUids = () => {
    const uids = new Set();
    (this.state.data_work_orders || []).forEach((wo) => {
      const bucket = this.getStatusBucketId(wo);
      if (bucket !== "completed" && bucket !== "cancelled" && wo?.asset?.uid) {
        uids.add(wo.asset.uid);
      }
    });
    return uids;
  };

  getLatestWarrantyExpireDate = (asset) => {
    const warranties = Array.isArray(asset?.customer_warranty) ? asset.customer_warranty : [];
    if (!warranties.length) return null;
    return warranties[warranties.length - 1]?.customer_warranty_expire_date || null;
  };

  getAssetCategoryLabel = (asset) => asset?.part?.part_category?.name || "-";

  getAssetLocationLabel = (asset) =>
    asset?.location_name || asset?.location_floor_room || asset?.location_address || "-";

  getAssetDisplayName = (asset) => asset?.part?.name || asset?.serial_number || asset?.asset_code || "-";

  formatBaht = (value) => `${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ฿`;

  getAxisTicks = (maxValue, steps = 5) => {
    const safeMax = maxValue > 0 ? maxValue : steps;
    const niceMax = Math.ceil(safeMax / steps) * steps || steps;
    const ticks = [];
    for (let i = steps; i >= 0; i--) ticks.push(Math.round((niceMax / steps) * i));
    return { ticks, niceMax };
  };

  getCategoryCounts = (assets) => {
    const map = new Map();
    assets.forEach((asset) => {
      const label = this.getAssetCategoryLabel(asset);
      map.set(label, (map.get(label) || 0) + 1);
    });
    return [...map.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  };

  // ต้นทุนอะไหล่/จำนวนใบสั่งงาน/เวลาตอบสนอง-ปิดงาน ต่อทรัพย์สิน 1 ชิ้น คำนวณจากใบสั่งงานในช่วงวันที่ที่เลือก
  getAssetMetrics = (filteredWorkOrders) => {
    const map = new Map();
    filteredWorkOrders.forEach((wo) => {
      const assetUid = wo?.asset?.uid;
      if (!assetUid) return;

      const entry = map.get(assetUid) || {
        assetUid,
        woCount: 0,
        partsCost: 0,
        completionDurations: [],
        responseDurations: [],
      };
      entry.woCount += 1;

      (wo.visits || []).forEach((visit) => {
        (visit.visits_items || []).forEach((vi) => {
          const qty = Number(vi.actual_quantity ?? vi.quantity) || 0;
          const price = Number(vi.price) || 0;
          entry.partsCost += qty * price;
        });
      });

      const createdAt = wo.created_at ? new Date(wo.created_at) : null;
      const endAt = wo.end_date ? new Date(wo.end_date) : null;
      const startAt = wo.start_date ? new Date(wo.start_date) : null;

      if (createdAt && endAt && !isNaN(createdAt) && !isNaN(endAt)) {
        entry.completionDurations.push((endAt - createdAt) / 3600000);
      }
      if (createdAt && startAt && !isNaN(createdAt) && !isNaN(startAt)) {
        entry.responseDurations.push((startAt - createdAt) / 3600000);
      }

      map.set(assetUid, entry);
    });
    return map;
  };

  // วันที่ปิดงานล่าสุด (สถานะ completed) ของแต่ละทรัพย์สิน ไม่จำกัดตามช่วงวันที่ที่เลือก
  getLastServiceDateMap = () => {
    const map = new Map();
    (this.state.data_work_orders || []).forEach((wo) => {
      const assetUid = wo?.asset?.uid;
      if (!assetUid || this.getStatusBucketId(wo) !== "completed" || !wo.end_date) return;

      const current = map.get(assetUid);
      if (!current || new Date(wo.end_date) > new Date(current)) {
        map.set(assetUid, wo.end_date);
      }
    });
    return map;
  };

  render() {
    const { data_assets, start_date, end_date } = this.state;
    const filteredWorkOrders = this.getFilteredWorkOrders();

    const totalAssets = data_assets.length;
    const activeCount = data_assets.filter((a) => a.is_active).length;
    const inactiveCount = totalAssets - activeCount;
    const underMaintenanceCount = this.getOpenWorkOrderAssetUids().size;

    const now = new Date();
    const criticalAlertsCount = data_assets.filter((asset) => {
      const expireDate = this.getLatestWarrantyExpireDate(asset);
      return expireDate && new Date(expireDate) < now;
    }).length;

    const statusDonutTotal = activeCount + inactiveCount;
    const activePct = statusDonutTotal ? (activeCount / statusDonutTotal) * 100 : 0;
    const donutGradient = statusDonutTotal
      ? `conic-gradient(#059669 0% ${activePct}%, #64748b ${activePct}% 100%)`
      : "#e9ecef";

    const categoryCounts = this.getCategoryCounts(data_assets);
    const categoryAxis = this.getAxisTicks(Math.max(0, ...categoryCounts.map((c) => c.count)));

    const assetMetricsMap = this.getAssetMetrics(filteredWorkOrders);
    const lastServiceMap = this.getLastServiceDateMap();

    const topCostAssets = [...assetMetricsMap.values()]
      .filter((entry) => entry.partsCost > 0)
      .sort((a, b) => b.partsCost - a.partsCost)
      .slice(0, 5)
      .map((entry) => {
        const asset = data_assets.find((a) => a.uid === entry.assetUid);
        return {
          uid: entry.assetUid,
          name: this.getAssetDisplayName(asset),
          category: this.getAssetCategoryLabel(asset),
          location: this.getAssetLocationLabel(asset),
          parts: this.formatBaht(entry.partsCost),
          total: this.formatBaht(entry.partsCost),
        };
      });

    const assetRows = [...data_assets]
      .sort((a, b) => String(a.asset_code || "").localeCompare(String(b.asset_code || "")))
      .slice(0, 10)
      .map((asset) => ({
        uid: asset.uid,
        id: asset.asset_code || "-",
        name: this.getAssetDisplayName(asset),
        category: this.getAssetCategoryLabel(asset),
        location: this.getAssetLocationLabel(asset),
        is_active: !!asset.is_active,
        last_pm: lastServiceMap.has(asset.uid) ? format_date(lastServiceMap.get(asset.uid), "dd/mm/yyyy", "th") : "-",
      }));

    let totalPartsCost = 0;
    const allCompletionDurations = [];
    const allResponseDurations = [];
    assetMetricsMap.forEach((entry) => {
      totalPartsCost += entry.partsCost;
      allCompletionDurations.push(...entry.completionDurations);
      allResponseDurations.push(...entry.responseDurations);
    });
    const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    const avgCompletionHours = avg(allCompletionDurations);
    const avgResponseHours = avg(allResponseDurations);
    const totalMaintenanceWo = filteredWorkOrders.filter((wo) => wo?.asset?.uid).length;

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">

          {/* Header Title */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1">{t("asset_report")}</h3>
              <div className="text-secondary">{t("asset_report_desc")}</div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div style={{ width: "260px" }}>
                <DateRangePicker
                  type="th"
                  className="form-control bg-white rounded-2 border-0 shadow-sm text-dark"
                  value={{ start_date, end_date }}
                  onChange={(range) =>
                    this.setState({
                      start_date: range.start_date,
                      end_date: range.end_date,
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Top KPI Cards (5 Columns) */}
          <div className="row g-3 mb-4">
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center">
                <div className="text-secondary text-14 fw-bold mb-1">{t("total_assets")}</div>
                <h1 className="fw-bold text-primary mb-0" style={{ fontSize: "3rem", color: "#0ea5e9" }}>{totalAssets}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#f0fdf4" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("active")}</div>
                <h1 className="fw-bold mb-0" style={{ color: "#22c55e", fontSize: "3rem" }}>{activeCount}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#fffbeb" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("under_maintenance")}</div>
                <h1 className="fw-bold mb-0" style={{ color: "#eab308", fontSize: "3rem" }}>{underMaintenanceCount}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#f8fafc" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("inactive")}</div>
                <h1 className="fw-bold text-secondary mb-0" style={{ fontSize: "3rem" }}>{inactiveCount}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#fef2f2" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("critical_alerts")}</div>
                <h1 className="fw-bold text-danger mb-0" style={{ fontSize: "3rem" }}>{criticalAlertsCount}</h1>
              </div>
            </div>
          </div>

          {/* Charts Row (2 Columns) */}
          <div className="row g-4 mb-4">

            {/* Assets by Status (Donut Chart) - active/inactive เป็นสถานะจริงเพียง 2 ค่าของ asset (is_active) */}
            <div className="col-lg-5">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white">
                <h6 className="fw-bold text-dark mb-4">{t("assets_by_status")}</h6>

                <div className="d-flex flex-column align-items-center justify-content-center h-100 pb-3">
                  <div
                    className="rounded-circle position-relative mb-4"
                    style={{
                      width: "220px", height: "220px",
                      background: donutGradient
                    }}
                  >
                    <div className="position-absolute top-50 start-50 translate-middle bg-white rounded-circle" style={{ width: "120px", height: "120px" }}></div>
                  </div>

                  <div className="d-flex justify-content-center gap-4 text-12 text-secondary w-100">
                    <div className="d-flex align-items-center"><div className="wpx-30 hpx-8 rounded me-2" style={{ backgroundColor: "#059669" }}></div> {t("active")} ({activeCount})</div>
                    <div className="d-flex align-items-center"><div className="wpx-30 hpx-8 rounded me-2" style={{ backgroundColor: "#64748b" }}></div> {t("inactive")} ({inactiveCount})</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Assets by Category (Bar Chart) - อ้างอิงจาก part.part_category ของ asset */}
            <div className="col-lg-7">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white">
                <h6 className="fw-bold text-dark mb-4">{t("assets_by_category")}</h6>

                <div className="d-flex flex-column h-100 justify-content-end pb-2">
                  <div className="position-relative w-100 pb-4" style={{ height: "220px" }}>

                    <div className="position-absolute top-0 start-0 d-flex flex-column justify-content-between h-100 text-secondary text-10 text-end pe-2" style={{ width: "30px", bottom: "25px" }}>
                      {categoryAxis.ticks.map((tick, idx) => (
                        <span key={idx}>{tick}</span>
                      ))}
                    </div>

                    <div className="position-absolute h-100 d-flex align-items-end justify-content-around border-bottom border-start ps-3" style={{ left: "30px", right: "0", bottom: "25px" }}>

                      <div className="w-100 h-100 d-flex flex-column justify-content-between position-absolute top-0 start-0 z-0">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className="border-top w-100" style={{ borderColor: "#f1f5f9" }}></div>
                        ))}
                      </div>

                      {categoryCounts.length > 0 ? (
                        categoryCounts.slice(0, 6).map((c) => (
                          <div key={c.label} className="z-1" style={{ width: "12%", height: `${(c.count / categoryAxis.niceMax) * 100}%`, backgroundColor: "#458ea8" }}></div>
                        ))
                      ) : (
                        <div className="text-secondary text-12 z-1">{t("no_data")}</div>
                      )}
                    </div>

                    <div className="d-flex justify-content-around position-absolute w-100 text-secondary text-12 ps-3" style={{ left: "30px", right: "0", bottom: "-25px" }}>
                      {categoryCounts.slice(0, 6).map((c) => (
                        <span key={c.label} style={{ width: "12%", textAlign: "center" }} className="text-truncate" title={c.label}>{c.label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* 4 Colored Metric Blocks - แทนที่ MTBF/MTTR/Uptime/OEE (ไม่มีข้อมูล downtime จริงในระบบ) ด้วยตัวชี้วัดที่คำนวณได้จริงจากใบสั่งงานของทรัพย์สินในช่วงวันที่ที่เลือก */}
          <div className="row g-3 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#208cba" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("total_wo")}</div>
                <h2 className="fw-bold mb-0">{totalMaintenanceWo}</h2>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#3b82f6" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("avg_response_time")}</div>
                <h2 className="fw-bold mb-0">{avgResponseHours !== null ? `${avgResponseHours.toFixed(1)} ${t("hrs")}` : "-"}</h2>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#4ade80" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("avg_completion_time")}</div>
                <h2 className="fw-bold mb-0">{avgCompletionHours !== null ? `${avgCompletionHours.toFixed(1)} ${t("hrs")}` : "-"}</h2>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#a855f7" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("parts_cost")}</div>
                <h2 className="fw-bold mb-0">{this.formatBaht(totalPartsCost)}</h2>
              </div>
            </div>
          </div>

          {/* Top Assets by Maintenance Cost Table */}
          <div className="card border-0 rounded-4 shadow-sm mb-4 overflow-hidden bg-white">
            <div className="p-3 border-bottom d-flex align-items-center" style={{ backgroundColor: "#fffcf2" }}>
              <span className="icon text-warning me-2 text-16">{""}</span>
              <h6 className="fw-bold text-danger mb-0" style={{ color: "#b45309" }}>{t("top_assets_cost")}</h6>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ backgroundColor: "#fffcf2" }}>
                  <tr className="text-dark fw-bold text-14">
                    <th className="py-3 px-4 border-0">{t("asset")}</th>
                    <th className="py-3 border-0">{t("category")}</th>
                    <th className="py-3 border-0">{t("location")}</th>
                    <th className="py-3 border-0">{t("labor_cost")}</th>
                    <th className="py-3 border-0">{t("parts_cost")}</th>
                    <th className="py-3 border-0">{t("total_cost")}</th>
                  </tr>
                </thead>
                <tbody>
                  {topCostAssets.length > 0 ? (
                    topCostAssets.map((item) => (
                      <tr key={item.uid} className="border-bottom bg-white">
                        <td className="py-3 px-4 fw-bold text-dark text-14">{item.name}</td>
                        <td className="text-dark text-14">{item.category}</td>
                        <td className="text-dark text-14">{item.location}</td>
                        <td className="text-secondary text-14">-</td>
                        <td className="text-dark text-14">{item.parts}</td>
                        <td className="fw-bold text-dark text-14">{item.total}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-secondary py-4">{t("no_data")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Asset List Table */}
          <div className="card border-0 rounded-4 shadow-sm mb-4 overflow-hidden bg-white">
            <div className="p-4 border-bottom">
              <h6 className="fw-bold text-dark mb-0">{t("asset_list")}</h6>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr className="text-dark fw-bold text-14">
                    <th className="py-3 px-4 border-0">{t("asset_id")}</th>
                    <th className="py-3 border-0">{t("name")}</th>
                    <th className="py-3 border-0">{t("category")}</th>
                    <th className="py-3 border-0">{t("location")}</th>
                    <th className="py-3 border-0">{t("status")}</th>
                    <th className="py-3 border-0">{t("last_pm")}</th>
                    <th className="py-3 border-0">{t("next_pm")}</th>
                  </tr>
                </thead>
                <tbody>
                  {assetRows.length > 0 ? (
                    assetRows.map((item) => (
                      <tr key={item.uid} className="border-bottom bg-white">
                        <td className="py-3 px-4 fw-bold text-dark text-14">{item.id}</td>
                        <td className="text-dark text-14">{item.name}</td>
                        <td className="text-dark text-14">{item.category}</td>
                        <td className="text-dark text-14">{item.location}</td>
                        <td>
                          <span className={`badge rounded-pill fw-bold text-12 px-3 py-1 border ${item.is_active ? "bg-success-light text-success border-success" : "bg-secondary-light text-secondary border-secondary"}`}>
                            {t(item.is_active ? "active" : "inactive")}
                          </span>
                        </td>
                        <td className="text-dark text-14">{item.last_pm}</td>
                        <td className="text-dark text-14">-</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary py-4">{t("no_data")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  }
}
